import { en } from "./dictionaries/en";
import { it } from "./dictionaries/it";

/**
 * Lightweight, type-checked i18n layer for the embedded admin UI.
 *
 * Why not a library (react-i18next / remix-i18next)? Polaris web components
 * (`s-*`) are custom elements rendered directly in JSX — there is no Polaris
 * AppProvider `i18n` prop to hook into (that's the older `@shopify/polaris`
 * React package; see notes/CODE-INDEX.md). A tiny typed dictionary + `t()`
 * keeps the dependency surface flat and, crucially, makes translation
 * COMPLETENESS a compile-time guarantee: `it` is declared `satisfies typeof en`
 * (see dictionaries/it/index.ts), so `tsc --noEmit` fails if any locale is
 * missing a key or has an extra one. `npm run typecheck` IS the parity test.
 */

export type Dict = typeof en;

export const SUPPORTED_LOCALES = ["en", "it"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: SupportedLocale = "en";

const DICTS: Record<SupportedLocale, Dict> = { en, it };

function isSupportedLocale(value: string): value is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Normalize a raw locale tag (e.g. "it-IT", "IT", "en_US") to one of our
 * supported base languages, falling back to English for anything else.
 */
export function resolveSupportedLocale(raw: string | null | undefined): SupportedLocale {
  if (!raw) return DEFAULT_LOCALE;
  const base = raw.trim().split(/[-_]/)[0]?.toLowerCase();
  return base && isSupportedLocale(base) ? base : DEFAULT_LOCALE;
}

type Interpolations = Record<string, string | number>;

// Replaces {{token}} placeholders. Unknown tokens are left as-is (visible bug
// signal in dev) rather than silently dropped.
function interpolate(template: string, vars?: Interpolations): string {
  if (!vars) return template;
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match,
  );
}

function getPath(dict: Dict, path: string): unknown {
  return path
    .split(".")
    .reduce<unknown>(
      (acc, part) => (acc && typeof acc === "object" ? (acc as Record<string, unknown>)[part] : undefined),
      dict,
    );
}

export type Translator = (path: string, vars?: Interpolations) => string;

/**
 * Build a `t(path, vars)` translator bound to a resolved locale. Falls back
 * key-by-key to English (defense in depth — the compile-time parity check
 * should make this unreachable in practice, but a bad merge shouldn't ever
 * render a blank string to a merchant).
 */
export function createTranslator(locale: SupportedLocale): Translator {
  const dict = DICTS[locale] ?? DICTS[DEFAULT_LOCALE];
  const fallbackDict = DICTS[DEFAULT_LOCALE];
  return function t(path, vars) {
    const value = getPath(dict, path);
    const resolved = typeof value === "string" ? value : getPath(fallbackDict, path);
    if (typeof resolved !== "string") {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn(`[i18n] missing translation key: ${path}`);
      }
      return path;
    }
    return interpolate(resolved, vars);
  };
}
